import { API_BASE, SUPABASE_ANON_KEY } from './apiBase';
import i18n from '@/i18n';

function generateRequestId(): string {
  return crypto.randomUUID();
}

function buildUrl(endpoint: string, params: Record<string, string>, reqId: string): string {
  const mode = import.meta.env.PROD ? 'prod' : 'dev';
  const baseUrl = import.meta.env.PROD 
    ? `${API_BASE}/articles-localized`
    : '/api/articles';
  
  const searchParams = new URLSearchParams(params);
  const fullUrl = `${baseUrl}?${searchParams.toString()}`;
  
  console.log(`[articlesApi] ${mode} url=${fullUrl} reqId=${reqId}`);
  return fullUrl;
}

async function fetchWithAuth(url: string, reqId: string) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Request-Id': reqId,
  };

  // Add Supabase auth headers for production
  if (import.meta.env.PROD) {
    if (SUPABASE_ANON_KEY) {
      headers['Authorization'] = `Bearer ${SUPABASE_ANON_KEY}`;
    }
  }

  console.log(`[articlesApi] Making request to: ${url} with headers:`, headers);
  
  try {
    const response = await fetch(url, { headers });
    console.log(`[articlesApi] Response status: ${response.status} ${response.statusText} reqId=${reqId}`);
    return response;
  } catch (error) {
    console.error(`[articlesApi] Fetch error for reqId=${reqId}:`, error);
    throw error;
  }
}

export async function getArticles(options?: {
  limit?: number;
  offset?: number;
  tag?: string;
  person?: string;
  question?: string;
  excludeBriefBullets?: boolean;
}) {
  const lang = (i18n.language as 'no' | 'en') || 'no';
  const reqId = generateRequestId();
  const params: Record<string, string> = { lang };
  
  // Default to a higher limit to show more than just recent Briefr Bullets
  if (options?.limit) params.limit = options.limit.toString();
  else params.limit = '50'; // Increased from default 10
  
  if (options?.offset !== undefined) params.offset = options.offset.toString();
  if (options?.tag) params.tag = options.tag;
  if (options?.person) params.person = options.person;
  if (options?.question) params.question = options.question;
  if (options?.excludeBriefBullets) params.excludeBriefBullets = 'true';
  
  const url = buildUrl('getArticles', params, reqId);
  const res = await fetchWithAuth(url, reqId);
  
  if (!res.ok) {
    throw new Error(`Failed to fetch articles: ${res.status} ${res.statusText}`);
  }
  
  const data = await res.json();
  
  // Handle both old format (direct array) and new format ({ items: [...] })
  const articles = data.items || data;
  
  // Debug logging for photo URLs
  console.log('[articlesApi] Articles debug:', {
    hasItems: !!data.items,
    totalArticles: articles?.length || 0,
    firstArticlePhotoUrl: articles?.[0]?.photo_url,
    hasFirstPhoto: !!(articles?.[0]?.photo_url)
  });
  
  if (Array.isArray(articles) && articles.length > 0 && articles[0].translated !== undefined) {
    console.log(`[articlesApi] Response translated=${articles[0].translated} reqId=${reqId}`);
  }
  
  return articles;
}

export async function getArticle(id: string) {
  const lang = (i18n.language as 'no' | 'en') || 'no';
  const reqId = generateRequestId();
  const params = { id, lang };
  const url = buildUrl('getArticle', params, reqId);
  const res = await fetchWithAuth(url, reqId);
  
  if (!res.ok) {
    throw new Error(`Failed to fetch article: ${res.status} ${res.statusText}`);
  }
  
  const data = await res.json();
  
  // Handle both old format (direct object) and new format ({ item: {...} })
  const article = data.item || data;
  
  // Debug logging for photo URL
  console.log('[articlesApi] Single article debug:', {
    hasItem: !!data.item,
    hasPhotoUrl: !!article?.photo_url,
    photoUrl: article?.photo_url,
    intervieweeName: article?.interviewee_name
  });
  
  if (article.translated !== undefined) {
    console.log(`[articlesApi] Response translated=${article.translated} reqId=${reqId}`);
  }
  
  return article;
}